"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";

import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface ComboboxOption {
    value: string;
    label: string;
}

export interface ComboboxProps {
    options: ComboboxOption[];
    value?: string;
    onValueChange?: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    className?: string;
    disabled?: boolean;
}

/**
 * Combobox Component
 *
 * Autocomplete input and command palette with a list of suggestions.
 * Built using a composition of Popover and Command components.
 *
 * @example
 * ```tsx
 * const frameworks = [
 *   { value: "next.js", label: "Next.js" },
 *   { value: "react", label: "React" },
 * ];
 *
 * <Combobox
 *   options={frameworks}
 *   placeholder="Select framework..."
 *   onValueChange={setValue}
 * />
 * ```
 */
export function Combobox({
    options,
    value,
    onValueChange,
    placeholder = "Select option...",
    searchPlaceholder = "Search...",
    emptyText = "No option found.",
    className,
    disabled = false,
}: ComboboxProps) {
    const [open, setOpen] = React.useState(false);

    const selectedOption = options.find((option) => option.value === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn("setu:w-[200px] setu:justify-between", className)}
                >
                    {selectedOption ? selectedOption.label : placeholder}
                    <ChevronsUpDown className="setu:ml-2 setu:h-4 setu:w-4 setu:shrink-0 setu:opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="setu:w-[200px] setu:p-0">
                <Command>
                    <CommandInput placeholder={searchPlaceholder} className="setu:h-9" />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    value={option.value}
                                    onSelect={(currentValue) => {
                                        const newValue = currentValue === value ? "" : currentValue;
                                        onValueChange?.(newValue);
                                        setOpen(false);
                                    }}
                                >
                                    {option.label}
                                    <Check
                                        className={cn(
                                            "setu:ml-auto setu:h-4 setu:w-4",
                                            value === option.value ? "setu:opacity-100" : "setu:opacity-0"
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
Combobox.displayName = "Combobox";
